import React from "react";

import { PERIOD_OPTIONS, type Program } from "../../shared/programMocks";
import { agentsForProgram } from "../agentsMocks";
import { holdersForProgram } from "../holdersMocks";

import { type ProgramMeter, ProgramProgress } from "./ProgramProgress";

interface ProgramProgressContainerProps {
    program: Program;
}

/**
 * ProgramProgressContainer — the **Container**. Owns the data seam: derives
 * `spent` from the program's participants (`agentsForProgram` /
 * `holdersForProgram`, target-driven) and `periodDays` / `daysElapsed` from
 * the program, resolves the budget + timeline meter specs, and renders the
 * props-only `ProgramProgress` View. Imports the View; the View imports no
 * data (firewall).
 *
 * Continuous programs get both meters `null` (the View then renders nothing).
 *
 * This is the primary entry `ProgramDetail` imports (same `program` prop).
 */
export function ProgramProgressContainer({
    program
}: ProgramProgressContainerProps): React.ReactElement {
    const isHolders = program.target === "holders";
    // Distributed rewards drive the budget "spent" meter; same source the
    // key-stats "USDv Distributed" figure uses, target-driven.
    const distributed = isHolders
        ? holdersForProgram(program.id).reduce((sum, h) => sum + h.rewards, 0)
        : agentsForProgram(program.id).reduce((sum, a) => sum + a.rewards, 0);

    const periodDays =
        program.periodDays ??
        PERIOD_OPTIONS.find((p) => p.value === program.period)?.days ??
        90;
    const spent = Math.round(distributed);
    const daysElapsed = Math.min(program.daysElapsed ?? 0, periodDays);
    const daysRemaining = Math.max(0, periodDays - daysElapsed);

    // Continuous programs have neither a budget cap nor a fixed timeline, so
    // they resolve no meters — the View then renders nothing (no empty
    // bordered band under the key-stats row).
    if (program.programType === "continuous") {
        return <ProgramProgress budgetMeter={null} timelineMeter={null} />;
    }

    // Budget meter — fixed-length, fixed-budget programs only (programType
    // defaults to fixed when unset).
    const budgetMeter: ProgramMeter | null =
        program.budgetMode !== "open"
            ? {
                  title: "Program budget",
                  fraction: spent / program.budget,
                  fillLabel: `${spent.toLocaleString("en-US")} USDv spent`,
                  trailLabel: `${program.budget.toLocaleString("en-US")} USDv total`,
                  // peony-500 isn't wired as a Tailwind utility (only base
                  // `peony` is); reference the CSS var directly rather than
                  // leak a token into shared config.
                  fillColor: "bg-[rgb(var(--color-peony-500))]"
              }
            : null;

    // Timeline meter — all fixed-length programs.
    const timelineMeter: ProgramMeter = {
        title: "Program timeline",
        fraction: program.status === "ended" ? 1 : daysElapsed / periodDays,
        fillLabel:
            program.status === "ended"
                ? "Program ended"
                : `${daysElapsed} days elapsed`,
        trailLabel:
            program.status === "ended"
                ? "Completed"
                : `${daysRemaining} days remaining`,
        fillColor: "bg-fig"
    };

    return (
        <ProgramProgress
            budgetMeter={budgetMeter}
            timelineMeter={timelineMeter}
        />
    );
}
